import React, { useCallback, useEffect, useRef } from "react";
import { pb } from "../lib/pocketbaseConfig";

type AuthProvider = {
  name: string;
  codeVerifier: string;
  authUrl: string;
};

type AuthMethodsResponse = {
  authProviders?: AuthProvider[];
};

export function GoogleOAuthCallbackHandler() {
  const handledRef = useRef(false);

  useEffect(() => {
    const finishGoogleLogin = async () => {
      if (handledRef.current) return;

      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");

      if (!code) return;

      handledRef.current = true;

      const raw = sessionStorage.getItem("pb_oauth");
      if (!raw) {
        console.error("Missing OAuth session");
        return;
      }

      try {
        const { provider, codeVerifier, redirectUrl, afterLoginPath } =
          JSON.parse(raw);

        const authData = await pb
          .collection("users")
          .authWithOAuth2Code(provider, code, codeVerifier, redirectUrl);

        const record = authData.record as any;
        const meta = (authData as any).meta || {};
        const rawUser = meta.rawUser || {};

        const fullName = (meta.name || rawUser.name || "").trim();

        const firstName = (
          record.firstName ||
          rawUser.given_name ||
          fullName.split(" ")[0] ||
          ""
        ).trim();

        const lastName = (
          record.lastName ||
          rawUser.family_name ||
          fullName.split(" ").slice(1).join(" ") ||
          ""
        ).trim();

        await pb.collection("users").update(record.id, {
          firstName,
          lastName,
          loginType: "google",
          isGuest: false,
        });

        sessionStorage.removeItem("pb_oauth");

        const finalUrl = `${window.location.origin}${afterLoginPath || "/profile"}`;
        window.location.href = finalUrl;
      } catch (err) {
        console.error("OAuth callback failed:", err);
        sessionStorage.removeItem("pb_oauth");
        window.location.href = `${window.location.origin}/profile`;
      }
    };

    finishGoogleLogin();
  }, []);

  return null;
}

export default function GoogleLoginButton() {
  const handleGoogleLogin = useCallback(async () => {
    try {
      const res = await fetch(
        `${pb.baseURL}/api/collections/users/auth-methods`,
      );

      if (!res.ok) {
        throw new Error(`Failed to load auth methods: ${res.status}`);
      }

      const methods: AuthMethodsResponse = await res.json();

      const google = methods.authProviders?.find(
        (p: AuthProvider) => p.name === "google",
      );

      if (!google) {
        console.error("Google auth provider not found");
        return;
      }

      const redirectUrl = `${window.location.origin}/`;

      sessionStorage.setItem(
        "pb_oauth",
        JSON.stringify({
          provider: google.name,
          codeVerifier: google.codeVerifier,
          redirectUrl,
          afterLoginPath: "/profile",
        }),
      );

      const authUrl = google.authUrl + encodeURIComponent(redirectUrl);
      window.location.href = authUrl;
    } catch (err) {
      console.error("Google login failed:", err);
    }
  }, []);

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <div
        onClick={handleGoogleLogin}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          border: "1px solid #dadce0",
          borderRadius: 6,
          padding: "10px 16px",
          cursor: "pointer",
          background: "#fff",
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          fontWeight: 500,
        }}
      >
        <img
          src="/assets/images/login/google.png" 
          alt="Google"
          style={{ width: 18, height: 18 }}
        />
        Вход с Google
      </div>
    </div>
  );
}
